import { Dispatch, SetStateAction, useState } from 'react';
import { QueryKey, UseQueryResult, useQuery } from 'react-query';
import { first, isEmpty, last, reverse } from 'lodash';

import { GetPositionScheduleLive } from '@pimm/services/lib/sms-workforce/services';
import { formatToISOString, stringToDateLocal } from '@app/utils/date-formatter';
import { DayBlock, useSiteTime } from '@app/features/store-core';

export type GetPositionScheduleLiveParams = {
  dayBlock: DayBlock;
  siteId?: string;
  subBlockStartTime?: Date;
  subBlockEndTime?: Date;
};

export const useGetPositionScheduleLive = (props?: {
  queryKey?: string;
  query?: GetPositionScheduleLiveParams;
}): [
  UseQueryResult<Awaited<ReturnType<typeof GetPositionScheduleLive>>>,
  Dispatch<SetStateAction<GetPositionScheduleLiveParams | undefined>>,
] => {
  const siteTime = useSiteTime();
  const [query, setQuery] = useState<GetPositionScheduleLiveParams | undefined>(props?.query);

  const liveSchedules = useQuery({
    cacheTime: 0,
    staleTime: 0,
    enabled: !!query?.siteId && !!query?.dayBlock.startTime && !!query?.dayBlock.endTime,
    queryKey: [props?.queryKey ?? 'GetPositionScheduleLive', query] as [QueryKey, GetPositionScheduleLiveParams],
    queryFn: async ({ queryKey: [key, { dayBlock, subBlockStartTime, subBlockEndTime, ...params }] }) => {
      const today = siteTime.today();
      let liveStartTime = subBlockStartTime ?? dayBlock.startTime;
      let isDayblockNow = false;

      // Check if the current dayBlock is now, change liveStartTime using store current time
      if (today >= dayBlock?.startTime && today < dayBlock.endTime) {
        const isPast = !!subBlockEndTime && subBlockEndTime <= today;
        liveStartTime = isPast ? subBlockStartTime ?? today : today;
        isDayblockNow = true;
      }

      let isoSubBlockStartTime = subBlockStartTime ? formatToISOString(subBlockStartTime) : undefined;
      // Try and fetch live position group
      const result = await GetPositionScheduleLive({
        ...params,
        subBlockDateTime: isoSubBlockStartTime,
        blockStartDateTime: formatToISOString(dayBlock.startTime),
        liveStartDateTime: formatToISOString(liveStartTime),
      });

      if (result) {
        // Find pre-selected shiftSubBlock
        if (!isEmpty(result.shiftSubBlocks) && !result.positionGroup?.subBlockTime) {
          let shiftSubBlock = result.shiftSubBlocks?.[0];

          if (isDayblockNow) {
            // prettier-ignore
            shiftSubBlock =  result.shiftSubBlocks?.toReversed().find(shift => {
              const startTime = stringToDateLocal(shift.subBlockDateTime);
              return startTime && startTime <= siteTime.today();
            }) ?? shiftSubBlock;
          }

          // Tell the ui to use this pre-selected shiftSubBlock startTime
          result.positionGroup = {
            subBlockTime: isoSubBlockStartTime ?? shiftSubBlock?.subBlockDateTime,
          };
        }
      }
      return result;
    },
  });

  return [liveSchedules, setQuery];
};
